// frontend/src/pages/Dashboard/index.jsx
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import api from "../../services/api";
import { Link } from "react-router-dom";
import styles from "./dashboard.module.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toAbsoluteUrl } from "../../utils/assetUrl";

export default function Dashboard() {
  // 1) Hooks: sempre no topo, sem retornos antes
  const { user, setUser, loadingApp, signOut } = useContext(AuthContext);
  const [loadingLocal, setLoadingLocal] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [nome, setNome] = useState(user?.nome || "");
  const [email, setEmail] = useState(user?.email || "");
  const [telefone, setTelefone] = useState(user?.telefone || "");
  const [endereco, setEndereco] = useState(user?.endereco || "");
  const [cpf, setCpf] = useState(user?.cpf || "");
  // Campo data de nascimento removido para alinhar com o schema atual
  const [senha, setSenha] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(user?.photoUrl || "");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // 2) Ao montar, buscar novamente /user/me (após loadingApp ficar false)
  useEffect(() => {
    async function fetchMe() {
      setLoadingLocal(true);
      try {
        const res = await api.get("/user/me");
        setUser({
          id: res.data.ID,
          login: res.data.LOGIN,
          nome: res.data.NOME,
          email: res.data.EMAIL,
          telefone: res.data.TELEFONE,
          endereco: res.data.ENDERECO,
          cpf: res.data.CPF,
          photoUrl: res.data.PHOTO_URL,
          dataCriacao: res.data.DATA_CRIACAO,
          dataAtualizacao: res.data.DATA_ATUALIZACAO,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingLocal(false);
      }
    }
    if (!loadingApp) {
      fetchMe();
    }
  }, [loadingApp, setUser]);

  // 3) Sincronizar estado local sempre que user mudar
  useEffect(() => {
    if (user) {
      setNome(user.nome || "");
      setEmail(user.email || "");
      setTelefone(user.telefone || "");
      setEndereco(user.endereco || "");
      setCpf(user.cpf || "");
      // dataNascimento removido do estado
      setPhotoPreview(user.photoUrl || "");
    }
  }, [user]);

  // 4) Enquanto carrega ou sem usuário, exibe mensagem apropriada
  if (loadingApp || loadingLocal) {
    return <div>Carregando dados …</div>;
  }
  if (!user) {
    return <div>Usuário não autenticado.</div>;
  }

  // 5) Handler de preview de foto
  function handlePhotoChange(e) {
    const file = e.target.files[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }

  // 6) Handler de atualização do perfil
  async function handleUpdate(e) {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      const formData = new FormData();
      formData.append("nome", nome);
      formData.append("email", email);
      formData.append("telefone", telefone);
      formData.append("endereco", endereco);
      formData.append("cpf", cpf);
      // data_nascimento removido, coluna não existe no schema informado
      if (senha.trim() !== "") {
        formData.append("senha", senha);
      }
      if (photoFile) {
        formData.append("photo", photoFile);
      }
      const response = await api.put(
        "/user/update",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const updatedUser = response.data.user;
      setUser({
        id: updatedUser.ID,
        login: updatedUser.LOGIN,
        nome: updatedUser.NOME,
        email: updatedUser.EMAIL,
        telefone: updatedUser.TELEFONE,
        endereco: updatedUser.ENDERECO,
        cpf: updatedUser.CPF,
        photoUrl: updatedUser.PHOTO_URL,
        dataCriacao: updatedUser.DATA_CRIACAO,
        dataAtualizacao: updatedUser.DATA_ATUALIZACAO,
      });
      toast.success("Dados atualizados com sucesso!");
      setMessage("Dados atualizados com sucesso!");
      setIsEditing(false);
      setSenha("");
      setPhotoFile(null);
    } catch (err) {
      console.error(err);
      const errMsg = err.response?.data?.error || "Erro ao atualizar seus dados.";
      setMessage(errMsg);
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }

  // 7) JSX final com dados do usuário e formulário de edição
  return (
    <main className={`${styles.root} ${styles["dashboard-principal"]}`}>
      <div className={styles["dashboard-container"]}>
        <header className={styles["dashboard-header"]}>
          <h1>Bem-vindo(a), {user.nome || user.login}!</h1>
          <button className={styles["btn-sair"]} onClick={signOut}>Sair</button>
        </header>

        <section className={styles["profile-section"]}>
          <div className={styles["profile-photo"]}>
            <img
              src={toAbsoluteUrl(photoPreview) || "/assets/sem-foto.png"}
              alt="Foto de perfil"
            />
          </div>

          <div className={styles["profile-info"]}>
            {!isEditing ? (
              <>
                <p><strong>Login:</strong> {user.login}</p>
                <p><strong>Nome:</strong> {user.nome || "-"}</p>
                <p><strong>Email:</strong> {user.email || "-"}</p>
                <p><strong>Telefone:</strong> {user.telefone || "-"}</p>
                <p><strong>Endereço:</strong> {user.endereco || "-"}</p>
                <p><strong>CPF:</strong> {user.cpf || "-"}</p>
                {/* Data de Nascimento removido para alinhar com a tabela */}
                <p>
                  <strong>Data de Criação:</strong>{" "}
                  {user.dataCriacao ? new Date(user.dataCriacao).toLocaleDateString() : "-"}
                </p>
                <p>
                  <strong>Última Atualização:</strong>{" "}
                  {user.dataAtualizacao ? new Date(user.dataAtualizacao).toLocaleString() : "-"}
                </p>
                <button className={styles["btn-editar"]} onClick={() => setIsEditing(true)}>
                  Editar Dados
                </button>
              </>
            ) : (
              <form
                className={styles["form-edicao"]}
                onSubmit={handleUpdate}
                encType="multipart/form-data"
              >
                <div className={styles["form-group"]}>
                  <label htmlFor="nome">Nome</label>
                  <input
                    type="text"
                    id="nome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    required
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="telefone">Telefone</label>
                  <input
                    type="text"
                    id="telefone"
                    value={telefone}
                    onChange={(e) => setTelefone(e.target.value)}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="endereco">Endereço</label>
                  <input
                    type="text"
                    id="endereco"
                    value={endereco}
                    onChange={(e) => setEndereco(e.target.value)}
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="cpf">CPF</label>
                  <input
                    type="text"
                    id="cpf"
                    value={cpf}
                    onChange={(e) => setCpf(e.target.value)}
                    placeholder="000.000.000-00"
                  />
                </div>

                {/* Campo de Data de Nascimento removido */}

                <div className={styles["form-group"]}>
                  <label htmlFor="senha">Nova Senha</label>
                  <input
                    type="password"
                    id="senha"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    placeholder="Deixe em branco para não alterar"
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label htmlFor="photo">Foto de Perfil</label>
                  <input
                    type="file"
                    id="photo"
                    accept="image/*"
                    onChange={handlePhotoChange}
                  />
                </div>

                <div className={styles["form-actions"]}>
                  <button
                    type="button"
                    className={styles["btn-cancelar"]}
                    onClick={() => {
                      setIsEditing(false);
                      setMessage("");
                      setPhotoFile(null);
                      setPhotoPreview(user.photoUrl || "");
                      setSenha("");
                      setNome(user.nome || "");
                      setEmail(user.email || "");
                      setTelefone(user.telefone || "");
                      setEndereco(user.endereco || "");
                      setCpf(user.cpf || "");
                      // dataNascimento removido
                    }}
                  >
                    Cancelar
                  </button>

                  <button type="submit" className={styles["btn-salvar"]} disabled={loading}>
                    {loading ? "Salvando…" : "Salvar"}
                  </button>
                </div>
              </form>
            )}

            {message && (
              <p className={`${styles.message} ${message.includes("Erro") ? styles.error : ''}`}>
                {message}
              </p>
            )}
          </div>
        </section>

        {/* Navegação interna com Link, para não recarregar a página */}
        <nav className={styles["dashboard-nav"]}>
          <ul>
            <li>
              <Link to="/Vendas">Minhas Vendas</Link>
            </li>
            <li>
              <Link to="/Clientes">Meus Clientes</Link>
            </li>
            <li>
              <Link to="/Financeiro">Financeiro</Link>
            </li>
            {/* etc. */}
          </ul>
        </nav>
        <ToastContainer position="bottom-right" autoClose={3000} />
      </div>
    </main>  
  );
}
